// Agent Types - Types for the LangGraph agent chat interface

// Tool call status
export type ToolCallStatus = 'pending' | 'running' | 'success' | 'error';

// Tool call detail
export interface ToolCallDetail {
  id: string;
  tool_name: string;
  arguments: Record<string, unknown>;
  result: string | null;
  status: ToolCallStatus;
  duration_ms: number | null;
  error_message: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

// LLM call detail - individual LLM call within the agent loop
export interface LLMCallDetail {
  id: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  latency_ms: number;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

// Message role
export type MessageRole = 'user' | 'assistant' | 'system' | 'tool';

// Chat message
export interface ChatMessage {
  role: MessageRole;
  content: string;
  created_at: string;
  metadata?: MessageMetadata;
}

// Message metadata
export interface MessageMetadata {
  model_provider?: string;
  model_name?: string;
  tokens_used?: number;
  cost_usd?: number;
  latency_ms?: number;
  tool_calls?: ToolCallDetail[];
  llm_calls?: LLMCallDetail[];
  reasoning_trace?: ReasoningTrace | null;
  request_metadata?: RequestMetadata;
}

// Reasoning trace
export interface ReasoningTrace {
  type: 'claude_thinking' | 'openai_reasoning';
  content: string;
}

// Request metadata - details about what was sent to the LLM
export interface RequestMetadata {
  user_message: string;
  system_prompt: string;
  mcp_servers: string[];
  available_tools: string[];
  conversation_history_length: number;
}

// History item types for the conversation history modal
export type HistoryItemType = 'user' | 'assistant' | 'tool_call' | 'tool_result' | 'llm_call';

// History item - flattened representation of conversation for detail view
export interface HistoryItem {
  type: HistoryItemType;
  content: string;
  timestamp: string;
  /** Elapsed time in ms since the start of the request when this item started */
  elapsedStartMs?: number;
  /** Elapsed time in ms since the start of the request when this item completed */
  elapsedEndMs?: number;
  metadata?: {
    tool_name?: string;
    tool_args?: Record<string, unknown>;
    tool_result?: string;
    tokens?: number;
    cost?: number;
    latency?: number;
    duration_ms?: number;
    // LLM call specific metadata
    llm_model?: string;
    llm_input_tokens?: number;
    llm_output_tokens?: number;
    llm_total_tokens?: number;
    llm_cost?: number;
    llm_latency?: number;
    llm_error?: string | null;
    llm_call_index?: number;
  };
  requestMetadata?: RequestMetadata;
}

// Agent response from API
export interface AgentResponse {
  message: string;
  model_provider: string;
  model_name: string;
  tokens_used: number | null;
  prompt_tokens?: number;
  completion_tokens?: number;
  cost_usd: number | null;
  latency_ms: number | null;
  tool_calls: ToolCallDetail[] | null;
  llm_calls: LLMCallDetail[] | null;
  reasoning_trace: ReasoningTrace | null;
  conversation_id: string;
  session_id?: string;
  request_metadata: RequestMetadata | null;
  created_at: string;
}

// Conversation summary
export interface ConversationSummary {
  id: string;
  title: string;
  message_count: number;
  model_provider: string | null;
  model_name: string | null;
  created_at: string;
  updated_at: string;
}

// Full conversation
export interface Conversation {
  id: string;
  user_id: string;
  agent_id: string;
  title: string;
  messages: ChatMessage[];
  message_count: number;
  created_at: string;
  updated_at: string;
}

// MCP server entry: plain URL string or dict with url + headers
export type MCPServerEntry = string | { url: string; headers?: Record<string, string> };

// Extract URL from an MCP server entry (string or dict)
export function getMCPServerUrl(entry: MCPServerEntry): string {
  return typeof entry === 'string' ? entry : entry.url;
}

// Extract URLs from a list of MCP server entries
export function getMCPServerUrls(entries: MCPServerEntry[]): string[] {
  return entries.map(getMCPServerUrl);
}

// Agent definition
export interface AgentDefinition {
  id: string;
  name: string;
  description: string | null;
  system_prompt: string;
  mcp_servers: MCPServerEntry[];
  default_model_provider: string;
  default_model_name: string;
  max_iterations: number;
  temperature: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Agent update request - all fields optional
export interface AgentUpdateRequest {
  name?: string;
  description?: string | null;
  system_prompt?: string;
  mcp_servers?: MCPServerEntry[];
  default_model_provider?: string;
  default_model_name?: string;
  max_iterations?: number;
  temperature?: number;
  is_active?: boolean;
}

// Tool info
export interface ToolInfo {
  name: string;
  description: string;
  mcp_server: string;
}

// MCP Server info - represents an MCP server that can be connected to
export interface MCPServerInfo {
  url: string;
  name: string;
  description: string;
  tool_count: number;
  status: 'connected' | 'disconnected' | 'error';
  tools?: string[];
}

// Tool set info (legacy - for backwards compatibility)
export interface ToolSetInfo {
  id: string;
  name: string;
  description: string;
  tools: string[];
  available: boolean;
}

// Chat request
export interface ChatRequest {
  message: string;
  agent_id?: string;
  conversation_id?: string;
  mcp_servers?: string[];
  enabled_tools?: string[];
  model_provider?: string;
  model_name?: string;
  conversation_history?: ChatMessage[];
}

// Model options for dropdown
export interface ModelOption {
  provider: string;
  model: string;
  label: string;
}

// Available model options
export const MODEL_OPTIONS: ModelOption[] = [
  // First entry is the default selection in AgentChatPage — keep it in
  // sync with the backend default (DEFAULT_OPENAI_MODEL in
  // services/agent/llm/clients.py).
  { provider: 'openai', model: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
  { provider: 'openai', model: 'gpt-5.4-mini', label: 'GPT-5.4 Mini' },
  { provider: 'openai', model: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
  { provider: 'openai', model: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
  { provider: 'openai', model: 'gpt-5-mini', label: 'GPT-5 Mini' },
  { provider: 'openai', model: 'gpt-5-nano', label: 'GPT-5 Nano' },
  { provider: 'anthropic', model: 'claude-sonnet-4-5-20250929', label: 'Claude 3.5 Sonnet' },
  { provider: 'anthropic', model: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
];
